import { useState } from 'react'
import { ExpandableRow } from '@carouselgg/nexus/expandable-row'
import { Check, Circle } from '@carouselgg/nexus/icon'
import type { TrustScoreCategories } from '@carouselgg/auth/types'
import { decayNote, type CategoryKey } from './trustCopy'
import styles from './CategoryBreakdown.module.css'

export interface CategoryRowCopy {
  name: string
  description: string
  /** "Every game" / "Per game" — whether this part carries across games. */
  tag?: string
}

/**
 * Score parts as rows that open onto what was earned and what is still
 * missing. Every number is the API's: the weights behind them are editable.
 */
export function CategoryBreakdown({ categories, keys, copyFor, label, openFirst = true }: {
  categories: TrustScoreCategories
  keys: readonly CategoryKey[]
  copyFor: (key: CategoryKey) => CategoryRowCopy
  /** Names the list for screen readers. */
  label: string
  openFirst?: boolean
}) {
  const shown = keys.filter((k) => categories[k])
  const [open, setOpen] = useState<Set<CategoryKey>>(() => new Set(openFirst && shown[0] ? [shown[0]] : []))

  const toggle = (key: CategoryKey, next: boolean) => {
    setOpen((prev) => {
      const s = new Set(prev)
      if (next) s.add(key)
      else s.delete(key)
      return s
    })
  }

  return (
    <div className={styles.list} role="list" aria-label={label}>
      {shown.map((key) => {
        const cat = categories[key]
        const copy = copyFor(key)
        const pct = cat.max > 0 ? Math.min(100, (cat.score / cat.max) * 100) : 0
        const earned = cat.items ?? []
        const missing = cat.missing_items ?? []
        const decay = decayNote(cat.decay_applied)
        return (
          <div role="listitem" key={key} className={styles.item}>
            <ExpandableRow
              className={styles.row}
              expanded={open.has(key)}
              onToggle={(next) => toggle(key, next)}
              toggleLabel={`${copy.name}: ${cat.score} of ${cat.max}`}
              summary={
                <div className={styles.summary}>
                  <span className={styles.titles}>
                    <span className={styles.name}>
                      {copy.name}
                      {copy.tag && <span className={styles.tag}>{copy.tag}</span>}
                    </span>
                    <span className={styles.description}>{copy.description}</span>
                  </span>
                  <span className={styles.bar} aria-hidden="true"><i className={styles.fill} style={{ width: `${pct}%` }} /></span>
                  <span className={styles.score}><b>{cat.score}</b><span className={styles.max}> / {cat.max}</span></span>
                </div>
              }
            >
              <ul className={styles.items}>
                {earned.map((item) => (
                  <li key={`e-${item.label}`} className={styles.earned}>
                    <Check size={16} className={styles.check} aria-label="Earned" />
                    <span className={styles.itemLabel}>{item.label}</span>
                    <span className={styles.points}>{item.points}</span>
                  </li>
                ))}
                {missing.map((item) => (
                  <li key={`m-${item.label}`} className={styles.missing}>
                    <Circle size={16} className={styles.circle} aria-label="Not yet" />
                    <span className={styles.itemLabel}>{item.label}</span>
                    <span className={styles.points}>+{item.points}</span>
                  </li>
                ))}
                {earned.length === 0 && missing.length === 0 && (
                  <li className={styles.empty}>Nothing counted here yet.</li>
                )}
              </ul>
              {decay && <p className={styles.decay}>{decay}</p>}
            </ExpandableRow>
          </div>
        )
      })}
    </div>
  )
}
